import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';

// =========================================================================
// MobileTabBar -- the app-like bottom navigation rail (phones only).
//
// Rendered as a `shrink-0` flex sibling DIRECTLY AFTER <main> inside the
// shell's main column (see `operatorShellMainColumnClass`). Because it is
// part of the normal flow -- NOT `position: fixed` -- <main> simply gets
// less height and stays the sole scroll surface. That means every page
// still scrolls all the way to its footer; the bar never overlaps or
// clips page content. Hidden at >=md where the desktop sidebar takes over.
//
// Labels are passed in already-resolved (so this file holds no literal UI
// strings and stays i18n-clean). Icons live here in one registry so both
// operator shells render an identical bar.
// =========================================================================

export interface MobileTab {
  /** Stable key for React. */
  key: string;
  label: string;
  icon: ReactNode;
  /** Navigation target (mutually exclusive with `onClick`). */
  to?: string;
  /** `end` matching for the NavLink (use for the "/" home tab). */
  end?: boolean;
  /** Action handler (e.g. the "Menu" tab that opens the drawer). */
  onClick?: () => void;
}

interface MobileTabBarProps {
  tabs: ReadonlyArray<MobileTab>;
  ariaLabel: string;
}

const itemBase =
  'flex h-full w-full flex-col items-center justify-center gap-1 px-1 py-1.5 ' +
  'text-[10px] font-medium leading-none tracking-tight transition-colors';

export function MobileTabBar({ tabs, ariaLabel }: MobileTabBarProps) {
  return (
    <nav
      aria-label={ariaLabel}
      className="md:hidden shrink-0 border-t border-rule bg-paper/95 backdrop-blur pb-[env(safe-area-inset-bottom)]"
    >
      <ul className="grid auto-cols-fr grid-flow-col">
        {tabs.map((tab) => (
          <li key={tab.key} className="min-h-[3.25rem]">
            {tab.to ? (
              <NavLink
                to={tab.to}
                end={tab.end}
                className={({ isActive }) =>
                  `${itemBase} ${isActive ? 'text-ink' : 'text-muted hover:text-ink'}`
                }
              >
                <span aria-hidden className="block">
                  {tab.icon}
                </span>
                <span className="block max-w-full truncate">{tab.label}</span>
              </NavLink>
            ) : (
              <button
                type="button"
                onClick={tab.onClick}
                className={`${itemBase} text-muted hover:text-ink`}
              >
                <span aria-hidden className="block">
                  {tab.icon}
                </span>
                <span className="block max-w-full truncate">{tab.label}</span>
              </button>
            )}
          </li>
        ))}
      </ul>
    </nav>
  );
}

// --- Icon registry -------------------------------------------------------
// 24x24 stroke icons rendered at 22px. Keyed by the nav route so the bar
// and the (text-only) sidebar stay in sync without per-module wiring.

function Svg({ children }: { children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-[22px] w-[22px]"
    >
      {children}
    </svg>
  );
}

export const HomeIcon = (
  <Svg>
    <path d="M3 10.5 12 4l9 6.5" />
    <path d="M5 9.5V20h14V9.5" />
    <path d="M9.5 20v-5h5v5" />
  </Svg>
);

export const MenuIcon = (
  <Svg>
    <circle cx="5" cy="12" r="1.4" />
    <circle cx="12" cy="12" r="1.4" />
    <circle cx="19" cy="12" r="1.4" />
  </Svg>
);

const ClientsIcon = (
  <Svg>
    <circle cx="9" cy="8" r="3" />
    <path d="M3.5 19a5.5 5.5 0 0 1 11 0" />
    <path d="M16 11a3 3 0 0 0 0-6" />
    <path d="M15.5 14.5a5.5 5.5 0 0 1 5 4.5" />
  </Svg>
);

const OrdersIcon = (
  <Svg>
    <path d="M7 3h7l4 4v14H7z" />
    <path d="M14 3v4h4" />
    <path d="M10 12h6M10 16h6" />
  </Svg>
);

const CalendarIcon = (
  <Svg>
    <rect x="4" y="5" width="16" height="16" rx="1.5" />
    <path d="M4 9h16M8 3v4M16 3v4" />
  </Svg>
);

const ProductsIcon = (
  <Svg>
    <path d="M12 3 4 7v10l8 4 8-4V7z" />
    <path d="M4 7l8 4 8-4M12 11v10" />
  </Svg>
);

const FulfillmentIcon = (
  <Svg>
    <path d="M3 7h11v9H3z" />
    <path d="M14 10h4l3 3v3h-7z" />
    <circle cx="7" cy="18" r="1.6" />
    <circle cx="17" cy="18" r="1.6" />
  </Svg>
);

const ReportsIcon = (
  <Svg>
    <path d="M4 20V4" />
    <path d="M4 20h16" />
    <path d="M8 16v-4M12 16V8M16 16v-7" />
  </Svg>
);

const WholesalersIcon = (
  <Svg>
    <path d="M4 20V6l8-3 8 3v14" />
    <path d="M9 20v-4h6v4" />
    <path d="M8 9h.01M12 9h.01M16 9h.01M8 12.5h.01M16 12.5h.01" />
  </Svg>
);

const VendorsIcon = (
  <Svg>
    <path d="M3 7h11v9H3z" />
    <path d="M14 10h4l3 3v3h-7z" />
    <circle cx="7" cy="18" r="1.6" />
    <circle cx="17" cy="18" r="1.6" />
  </Svg>
);

const ContractsIcon = (
  <Svg>
    <path d="M7 3h7l4 4v14H7z" />
    <path d="M14 3v4h4" />
    <path d="M10 18l1.5 1.5L15 16" />
  </Svg>
);

const ShippingIcon = VendorsIcon;
const LotsIcon = ProductsIcon;

const RewardsIcon = (
  <Svg>
    <rect x="4" y="9" width="16" height="11" rx="1" />
    <path d="M3 9h18M12 9v11" />
    <path d="M12 9S9 4 7 5.5 9 9 12 9zM12 9s3-5 5-3.5S15 9 12 9z" />
  </Svg>
);

const MarketingIcon = (
  <Svg>
    <path d="M4 10v4l11 5V5z" />
    <path d="M15 8a3 3 0 0 1 0 8" />
    <path d="M7 14v4" />
  </Svg>
);

const DotIcon = (
  <Svg>
    <circle cx="12" cy="12" r="7" />
  </Svg>
);

const ICON_BY_ROUTE: Record<string, ReactNode> = {
  '/clients': ClientsIcon,
  '/orders': OrdersIcon,
  '/calendar': CalendarIcon,
  '/products': ProductsIcon,
  '/lots': LotsIcon,
  '/fulfillment': FulfillmentIcon,
  '/reports': ReportsIcon,
  '/wholesalers': WholesalersIcon,
  '/vendors': VendorsIcon,
  '/contracts': ContractsIcon,
  '/shipping': ShippingIcon,
  '/rewards': RewardsIcon,
  '/marketing': MarketingIcon,
};

/** Icon for a module nav route, falling back to a neutral dot. */
export function iconForRoute(to: string): ReactNode {
  return ICON_BY_ROUTE[to] ?? DotIcon;
}
